import Icon from 'components/Icon';
import { parse } from 'query-string';
import { useContext, useEffect, useState } from 'react';
import TaskEditorCont, {
  OwnProps as ITaskEditorCont,
} from 'containers/Tasks/TasksManagement/TaskEditorCont';
import { setSingleTaskAccessToken } from 'store/session/SessionSlice';
import store, { appDispatch, RootState } from 'store/store';
import { getSingleTask } from 'store/tasks/tasksSlice';
import { ITask } from 'store/tasks/tasksType';
import { /*globalHistory,*/ navigate } from '@reach/router';
import { useWindowSize } from 'hooks/useOnwindowSizeChange';
import messages from './Messages';
import { shallowEqual, useSelector } from 'react-redux';
import { Background } from './styles';
import i18n, { languageDirection } from '../../../i18n';
import { OpenItemsContext } from '../../../store/context/OpenItemsContext';
import { black, exactWhite } from 'utils/style/colors';

interface IAutoFillField {
  [key: string]: any;
}

interface ITaskEditorStack
  extends Omit<ITaskEditorCont, 'tab' | 'task' | 'windowSize'> {
  taskId?: number | 'new';
  hideParents?: boolean;
  taskLeftOffset?: number;
  autoFillFields?: IAutoFillField;
  disableStack?: boolean | undefined;
}

interface INewTask {
  id: 'new';
  parentId?: number;
}

const minTaskWidthPx = 400;
const mobileScreenSize = 600;
const maxTaskWidthPx = 1000;
const minOverlayWidth = 100;
const TaskEditorStack = (props: ITaskEditorStack) => {
  const cntx = useContext(OpenItemsContext);

  const { taskId, ...rest } = props;
  const [tasks, setTasks] = useState<
    Array<{
      task: ITask | INewTask;
      order: number;
    }>
  >([]);
  const [toHideTasks, setToHideTasks] = useState<
    Array<{
      task: ITask | INewTask;
      order: 200;
    }>
  >([]);
  const [hideParents, setHideParents] = useState(
    props.hideParents === undefined ? true : props.hideParents
  );
  // const [canGoBack, setCanGoBack] = useState(true);
  const [isTT, setIsTT] = useState(false);
  const rightPadding = isTT ? 0 : 100;
  const { width } = useWindowSize();
  const isMobile = width <= mobileScreenSize;
  const state = store.getState() as RootState;
  const tasksData = useSelector(
    (store: RootState) => {
      return store.tasks.data;
    },
    {
      equalityFn: shallowEqual,
    }
  );
  const thisUser = useSelector(
    (store: RootState) => {
      return store.thisUser;
    },
    {
      equalityFn: shallowEqual,
    }
  );
  // const tasksData = state.tasks.data;
  const isSidebarCollapsed = state.app.UI.sidebar.isSideBarCollapsed;

  const parsed = parse(window.location!.search);
  const sidePanelWidth = !isSidebarCollapsed ? 255 : width < 800 ? 0 : 60;
  let finalTaskWidth = minTaskWidthPx;
  const availableFullscreenTaskCount = Math.min(
    tasks.length,
    Math.floor(
      (width - sidePanelWidth - rightPadding - minOverlayWidth * tasks.length) /
        (minTaskWidthPx - minOverlayWidth)
    )
  );
  let fullScreenTaskCont = hideParents ? 1 : availableFullscreenTaskCount;
  const minOverlayWidthWhenParentsHidden =
    availableFullscreenTaskCount < 2 ? 0 : 50;
  finalTaskWidth = Math.min(
    maxTaskWidthPx,
    (width -
      sidePanelWidth -
      rightPadding -
      (tasks.length - fullScreenTaskCont) *
        (hideParents ? minOverlayWidthWhenParentsHidden : minOverlayWidth)) /
      (fullScreenTaskCont || 1)
  );
  let extraTaskOffsetMultiplier = hideParents
    ? minOverlayWidthWhenParentsHidden
    : Math.min(
        minTaskWidthPx,
        (width -
          sidePanelWidth -
          rightPadding -
          fullScreenTaskCont * finalTaskWidth) /
          (tasks.length - fullScreenTaskCont || 1)
      );

  if (fullScreenTaskCont <= 0) {
    fullScreenTaskCont = 1;
    finalTaskWidth = width - sidePanelWidth;

    extraTaskOffsetMultiplier = Math.max(
      0,
      (width - minTaskWidthPx - 100) / (tasks.length - 1)
    );
  }
  if (width < minTaskWidthPx || finalTaskWidth < minTaskWidthPx) {
    finalTaskWidth = width - sidePanelWidth;
    fullScreenTaskCont = 1;
    extraTaskOffsetMultiplier = 0;
  }
  const totalModalWidth =
    fullScreenTaskCont * finalTaskWidth +
    (props.disableStack
      ? 0
      : (tasks.length - fullScreenTaskCont) * extraTaskOffsetMultiplier);

  function setTasksAndHiddenTasks(
    template: Array<{ task: ITask | INewTask; order: number }>
  ) {
    const toHideTasks = tasks.filter(
      (it) =>
        !template.find((c) => {
          return it.task.id === c.task.id;
        })
    );
    const toHideTasks2 = toHideTasks.map((it) => {
      it.order = 200;
      return it as {
        task: ITask | INewTask;
        order: 200;
      };
    });
    setTasks(template);
    if (
      (toHideTasks2.length !== tasks.length || tasks.length === 0) &&
      template.length !== tasks.length
    ) {
      if (toHideTasks2.length === 0) {
        const newTasks = template.filter(
          (it) =>
            !tasks.find((c) => {
              return it.task.id === c.task.id;
            })
        );
        const currentTasks = template.filter(
          (it) =>
            !!tasks.find((c) => {
              return it.task.id === c.task.id;
            })
        );
        if (newTasks.length > 0) {
          setToHideTasks(
            newTasks.map((it) => {
              const ret = { ...it };
              ret.order = 200;
              return ret as any;
            })
          );
          setTasks(currentTasks);
          setTimeout(() => {
            setTasks(template);
            setToHideTasks([]);
          }, 20);
        }
      } else {
        setToHideTasks(toHideTasks2);
        setTimeout(() => {
          setToHideTasks([]);
        }, 1000);
      }
    }
  }

  function fetchTaskData(isNew?: boolean) {
    if (parsed?.tt) {
      appDispatch(
        setSingleTaskAccessToken((parsed.tt as string).replace(' ', '+'))
      );
      setIsTT(true);
    }

    let missingParent = null;
    let condidId = isNew ? props.parentId : Number(taskId);
    const template: Array<{
      task: ITask | INewTask;
      order: number;
    }> = isNew
      ? [
          {
            task: { id: 'new', parentId: props.parentId },
            order: 101,
          },
        ]
      : [];
    while (condidId) {
      const foundTask = tasksData[condidId];
      if (foundTask) {
        template.push({
          task: foundTask,
          order: template.at(-1)
            ? template.at(-1)!.order - 1
            : isNew
              ? 100
              : 101,
        });
        condidId = foundTask.parentId;
      } else {
        missingParent = condidId;
        break;
      }
    }
    if (
      (!missingParent || !thisUser.isLoggedIn) &&
      missingParent !== Number(taskId)
    ) {
      setTasksAndHiddenTasks(template);
    } else {
      appDispatch(
        getSingleTask({
          taskId: missingParent!,
          includeParentTasks: true,
          includeAssignmentHistory: true,
        })
      ).then(
        ({ task }: { task: ITask }) => {
          template.push({
            order:
              template.length === 0
                ? isNew
                  ? 100
                  : 101
                : template.at(-1)
                  ? template.at(-1)!.order - 1
                  : isNew
                    ? 100
                    : 101,
            task: task,
          });

          // if (task.parents && task.parents.length) {
          //   // had been controlled with continue and break
          //   // eslint-disable-next-line no-constant-condition
          //   while (true) {
          //     if (template.at(-1)?.task?.parentId) {
          //       const parentTask = task.parents.find(
          //         (it) => it.id === template.at(-1)?.task?.parentId
          //       )!;
          //       template.push({
          //         task: parentTask,
          //         order: template.at(-1) ? template.at(-1)!.order - 1 : 101,
          //       });
          //       continue;
          //     }
          //     break;
          //   }
          // }
          setTasksAndHiddenTasks(template);
        },
        () => {
          setTasksAndHiddenTasks(template);
        }
      );
    }
  }

  function onCloseTask(parentId?: number, goUp: boolean = false) {
    const currentUrl = window.location.href;
    if (props.directTask) {
      if (parentId) {
        const found = tasks.find((it) => it.task.id === parentId);
        if (found)
          navigate(
            `/t/${props.companyId}/dt/${parentId}?tt=${(found.task as ITask).viewToken}`
          );
      }
      return;
    }
    if (currentUrl.includes('home') && !props.embedded) {
      // window.history.back();
      // console.log(currentUrl);
      navigate(`/t/${props.companyId}/home`);
    } else {
      if (props.embedded) {
        if (parentId) props?.handleChangeTaskInplace?.(parentId);
        else props.onCloseTask?.();
        return;
      }
      const projectPart =
        window.location.href?.includes('/project/') && props.projectId
          ? `project/${props.projectId}/`
          : '';
      let url = `/t/${props.companyId}/${projectPart}task`;
      if (parentId && (!hideParents || goUp)) {
        url = `${url}/editor/${parentId}/info`;
      }
      navigate(url);
    }
  }

  const [delayRenderParents, setDelayRenderParents] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setDelayRenderParents(false);
    }, 2000);
  });
  /* needs to check parent and add new, needs projectId as well */
  useEffect(() => {
    if (taskId) {
      fetchTaskData(taskId.toString().toLowerCase() === 'new');
    }
  }, [taskId, tasksData]);

  let sortedTasks = [...tasks, ...toHideTasks].sort((a, b) => {
    return b.order - a.order;
  });

  sortedTasks = props.disableStack ? sortedTasks.slice(0, 1) : sortedTasks;

  const taskLeftOffset = props.taskLeftOffset || 0;
  const side = languageDirection() === 'rtl' ? 'left' : 'right';

  return (
    <>
      <Background />
      <div
        className="h-full flex flex-row items-baseline overflow-x-hidden w-full"
        style={{
          maxWidth: isTT || isMobile ? '100vw' : `${totalModalWidth}px`,
        }}
      >
        {sortedTasks.length !== 0 ? (
          <>
            {sortedTasks.map((task, totalIndex) => {
              const index = Math.max(totalIndex - toHideTasks.length, 0);
              if (index > 0 && delayRenderParents) {
                return <div key={`task-${task.task.id}`} />;
              }
              const projectId =
                task.task.id === 'new' && task.task?.parentId
                  ? (sortedTasks[1]?.task as ITask)?.projectId
                  : (task.task as ITask).projectId;
              const left =
                task.order === 200
                  ? -finalTaskWidth - 30
                  : Math.min(index, fullScreenTaskCont - 1) * finalTaskWidth +
                    Math.max(index - fullScreenTaskCont + 1, 0) *
                      extraTaskOffsetMultiplier;
              return (
                <div
                  key={`task-${task.task.id}`}
                  className={`self-stretch h-full top-0 ${isTT ? 'fixed' : 'absolute'} `}
                  style={{
                    zIndex: 10000 + task.order,
                    [side]: `${left + taskLeftOffset}px`,
                    width: isMobile ? '100vw' : `${finalTaskWidth}px`,
                    transition: 'all 0.5s cubic-bezier(0, 0, 0, 1)',
                    filter: index === 0 || !hideParents ? '' : 'blur(2px)',
                    pointerEvents:
                      index === 0 || !hideParents ? undefined : 'none',
                    height:
                      cntx.openItems.length > 0
                        ? `calc( 100% + 1rem - ${index * 40}px)`
                        : `calc( 100% - ${index * 40}px)`,
                    marginTop: `${index * 20}px`,
                    borderRadius: isMobile ? 0 : '1.25rem',
                  }}
                >
                  <TaskEditorCont
                    {...rest}
                    taskId={task.task.id}
                    task={task.task.id === 'new' ? undefined : task.task}
                    templateId={props.templateId}
                    projectId={projectId || props.projectId}
                    onCloseTask={() => onCloseTask(task.task.parentId, true)}
                    stickToSide={index > 0}
                    selectedSubTaskId={
                      totalIndex > 0
                        ? sortedTasks[totalIndex - 1].task.id
                        : undefined
                    }
                    windowSize={
                      finalTaskWidth > 900
                        ? 'large'
                        : finalTaskWidth > 650
                          ? 'medium'
                          : 'small'
                    }
                    isMobile={isMobile}
                    autoFillFields={props.autoFillFields}
                  />
                </div>
              );
            })}

            {!isTT && !isMobile && (
              <div
                key={'up'}
                className={'absolute cursor-pointer'}
                style={{
                  top: !isMobile && width <= 550 ? 40 : 20,
                  [side]: `${totalModalWidth + taskLeftOffset - 5}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.5s cubic-bezier(0, 0, 0, 1)',
                  zIndex: '10000',
                }}
                onClick={() => {
                  // props.onCloseTask?.();
                  onCloseTask(tasks[tasks.length - 1].task.parentId, false);
                }}
              >
                <Icon
                  type={'x-circle'}
                  style={{
                    fontSize: '25px',
                    backgroundColor: exactWhite,
                    borderRadius: '23px',
                    padding: '5px',
                  }}
                />
              </div>
            )}
            {!props.disableStack && tasks.length > 1 && (
              <>
                {!isMobile && availableFullscreenTaskCount > 1 && (
                  <div
                    key={'expand'}
                    className={
                      'absolute cursor-pointer rotate-90 rounded-lg bg-gray-400 bg-opacity-20 hover:bg-opacity-90 hover:text-white'
                    }
                    style={{
                      zIndex: 10100,
                      top: 165,
                      [side]: `${
                        finalTaskWidth +
                        taskLeftOffset -
                        (hideParents ? 70 : 25)
                      }px`,
                      display: 'flex',
                      justifyContent: 'space-evenly',
                      alignItems: 'center',
                      transition: 'all 0.5s cubic-bezier(0, 0, 0, 1)',
                      width: hideParents ? '180px' : '100px',
                      padding: '0.75rem',
                      margin: '0.75rem 0',
                      color: black,
                    }}
                    onClick={() => {
                      setHideParents((prev) => !prev);
                    }}
                  >
                    <Icon
                      style={{
                        transition: 'transform 0.5s cubic-bezier(0, 0, 0, 1)',
                      }}
                      type={hideParents ? 'maximize-2' : 'minimize-2'}
                    />
                    <p className="whitespace-nowrap">
                      {hideParents
                        ? messages.showParents(
                            (tasks[1]?.task as ITask)?.taskName ||
                              (tasks[1]?.task as ITask)?.title
                          )
                        : (i18n.t('hideParents', {
                            ns: 'taskManager',
                          }) as string)}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};

export default TaskEditorStack;
